/**
 * Toast Context
 *
 * Global state management for toast notifications using React Context.
 * Manages FIFO stack of toasts with auto-dismiss timers.
 */

import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  useRef,
  useMemo,
} from 'react';
import { Toast, ToastType, ToastOptions } from '@/types/toast.types';

/**
 * Maximum number of visible toasts
 * Older toasts are auto-dismissed when limit is reached
 */
const MAX_TOASTS = 5;

/**
 * Default auto-dismiss durations by type (milliseconds)
 */
const DEFAULT_DURATIONS: Record<ToastType, number | null> = {
  [ToastType.SUCCESS]: 3000,
  [ToastType.ERROR]: 5000,
  [ToastType.INFO]: 4000,
  [ToastType.WARNING]: 5000,
  [ToastType.LOADING]: null, // Manual dismiss only
};

/**
 * Toast context value
 */
interface ToastContextValue {
  /** Current stack of toasts (FIFO) */
  toasts: Toast[];

  /** Show a success toast */
  success: (title: string, options?: ToastOptions) => string;

  /** Show an error toast */
  error: (title: string, options?: ToastOptions) => string;

  /** Show a loading toast (manual dismiss) */
  loading: (title: string, options?: ToastOptions) => string;

  /** Show an info toast */
  info: (title: string, options?: ToastOptions) => string;

  /** Show a warning toast */
  warning: (title: string, options?: ToastOptions) => string;

  /** Update an existing toast by ID */
  update: (
    id: string,
    updates: Partial<Pick<Toast, 'type' | 'title' | 'message' | 'duration'>>
  ) => void;

  /** Dismiss a toast by ID */
  dismiss: (id: string) => void;

  /** Dismiss all toasts */
  dismissAll: () => void;
}

const ToastContext = createContext<ToastContextValue | undefined>(undefined);

/**
 * Toast Provider Component
 */
export function ToastProvider({ children }: { children: React.ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([]);
  const timersRef = useRef<Map<string, NodeJS.Timeout>>(new Map());

  /**
   * Generate unique toast ID
   */
  const generateId = useCallback(() => {
    return `toast-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
  }, []);

  /**
   * Dismiss a toast by ID
   */
  const dismiss = useCallback((id: string) => {
    setToasts(prev => prev.filter(toast => toast.id !== id));

    // Clear timer
    const timer = timersRef.current.get(id);
    if (timer) {
      clearTimeout(timer);
      timersRef.current.delete(id);
    }
  }, []);

  /**
   * Schedule auto-dismiss for a toast
   */
  const scheduleAutoDismiss = useCallback(
    (id: string, duration: number) => {
      // Clear existing timer if any
      const existingTimer = timersRef.current.get(id);
      if (existingTimer) {
        clearTimeout(existingTimer);
      }

      // Schedule new timer
      const timer = setTimeout(() => {
        dismiss(id);
        timersRef.current.delete(id);
      }, duration);

      timersRef.current.set(id, timer);
    },
    [dismiss]
  );

  /**
   * Add a new toast to the stack
   */
  const addToast = useCallback(
    (type: ToastType, title: string, options?: ToastOptions): string => {
      const id = options?.id || generateId();
      const duration =
        options?.duration !== undefined
          ? options.duration
          : DEFAULT_DURATIONS[type];

      const newToast: Toast = {
        id,
        type,
        title,
        message: options?.message,
        duration,
        action: options?.action,
        created_at: Date.now(),
      };

      setToasts(prev => {
        // Remove oldest toast if at max capacity
        const toastsToKeep =
          prev.length >= MAX_TOASTS
            ? prev.slice(1) // Remove oldest (first in array)
            : prev;

        // Add new toast to end (newest)
        return [...toastsToKeep, newToast];
      });

      // Schedule auto-dismiss if duration is set
      if (duration !== null && duration > 0) {
        scheduleAutoDismiss(id, duration);
      }

      return id;
    },
    [generateId, scheduleAutoDismiss]
  );

  /**
   * Dismiss all toasts
   */
  const dismissAll = useCallback(() => {
    setToasts([]);

    // Clear all timers
    timersRef.current.forEach(timer => clearTimeout(timer));
    timersRef.current.clear();
  }, []);

  /**
   * Update an existing toast
   */
  const update = useCallback(
    (
      id: string,
      updates: Partial<Pick<Toast, 'type' | 'title' | 'message' | 'duration'>>
    ) => {
      setToasts(prev =>
        prev.map(toast => {
          if (toast.id !== id) return toast;

          const updatedToast = { ...toast, ...updates };

          // If duration changed, reschedule auto-dismiss
          if (updates.duration !== undefined) {
            const newDuration = updates.duration;

            // Clear existing timer
            const existingTimer = timersRef.current.get(id);
            if (existingTimer) {
              clearTimeout(existingTimer);
              timersRef.current.delete(id);
            }

            // Schedule new timer if duration is set
            if (newDuration !== null && newDuration > 0) {
              scheduleAutoDismiss(id, newDuration);
            }
          }

          return updatedToast;
        })
      );
    },
    [scheduleAutoDismiss]
  );

  /**
   * Convenience methods for each toast type
   */
  const success = useCallback(
    (title: string, options?: ToastOptions) =>
      addToast(ToastType.SUCCESS, title, options),
    [addToast]
  );

  const error = useCallback(
    (title: string, options?: ToastOptions) =>
      addToast(ToastType.ERROR, title, options),
    [addToast]
  );

  const loading = useCallback(
    (title: string, options?: ToastOptions) =>
      addToast(ToastType.LOADING, title, options),
    [addToast]
  );

  const info = useCallback(
    (title: string, options?: ToastOptions) =>
      addToast(ToastType.INFO, title, options),
    [addToast]
  );

  const warning = useCallback(
    (title: string, options?: ToastOptions) =>
      addToast(ToastType.WARNING, title, options),
    [addToast]
  );

  const value = useMemo<ToastContextValue>(
    () => ({
      toasts,
      success,
      error,
      loading,
      info,
      warning,
      update,
      dismiss,
      dismissAll,
    }),
    [
      toasts,
      success,
      error,
      loading,
      info,
      warning,
      update,
      dismiss,
      dismissAll,
    ]
  );

  return (
    <ToastContext.Provider value={value}>{children}</ToastContext.Provider>
  );
}

/**
 * Hook to access toast context
 * @throws Error if used outside ToastProvider
 */
// eslint-disable-next-line react-refresh/only-export-components -- context+hook co-location pattern
export function useToastContext(): ToastContextValue {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error('useToastContext must be used within ToastProvider');
  }
  return context;
}
